<template>
  <section class="flex flex-col mb-20" :aria-label="$t('checkout.order-btn')">
    <button
      :class="createOrder? '' : 'mt-10'"
      class="bg-redish text-white uppercase text-sm font-bold py-3 cursor-pointer"
      @click="createOrder ? saveOrder() : payOrder(orderSaved)"
      v-if="!workingOn && billingAddress && ((paymentMethod && paymentMethod.slug) || totalToPay <= 0)"
    >
      {{ $t('checkout.order-btn') }}
    </button>
    <button
      type="button"
      class="flex align-middle bg-redish text-white uppercase text-sm font-bold py-3"
      disabled
      v-if="workingOn"
    >
      <svg class="animate-spin h-5 w-5 mx-3" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      {{ $t('checkout.processing') }}
    </button>

  </section>
</template>

<script>
import { createOrder, addOrderDetails } from '@/services/OrdersService';
import axios from 'axios';
import { mapGetters } from 'vuex';
// eslint-disable-next-line no-undef
const stripe = Stripe(process.env.VUE_APP_STRIPE_KEY);

export default {
  props: ['value', 'givenMessage', 'selectedShippingAddress', 'paymentMethod', 'stripePaymentMethod', 'createOrder', 'order', 'couponCode'],

  data() {
    return {
      workingOn: this.value,
      message: this.givenMessage,

      orderSaved: this.order ? this.order : null,
    };
  },

  computed: {
    ...mapGetters({
      customer: ['auth/customer'],
      user: ['auth/user'],
      billingAddress: ['auth/billingAddress'],
      cartProducts: ['cart/products'],
      totalWithTaxes: ['cart/totalWithTaxes'],
      totalToPay: ['cart/totalToPay'],
      totalWithoutCoupon: ['cart/totalWithoutCoupon'],
      availableCoupon: ['cart/availableCoupon'],
    }),
  },

  watch: {
    workingOn() {
      this.$emit('input', this.workingOn);
    },
    message() {
      this.$emit('updatedMessage', this.message);
    },
  },

  methods: {
    persistOrder() {
      const order = {
        total: this.$store.getters['cart/total'],
        total_with_taxes: this.$store.getters['cart/totalToPay'],
        rounded_total_with_taxes: this.$store.getters['cart/totalToPay'],
        total_products: this.$store.getters['cart/subTotal'],
        total_products_with_taxes: this.$store.getters['cart/subTotalToPay'],
        total_shipping: this.$store.getters['cart/shippingPrice'],
        total_shipping_with_taxes: this.$store.getters['cart/shippingToPay'],
        subtotal_without_coupon: this.$store.getters['cart/subTotalWithTaxes'],
        subtotal_with_coupon: this.$store.getters['cart/subTotalToPay'],
        total_to_pay: this.$store.getters['cart/totalToPay'],
        billing_address_id: this.billingAddress.id,
        shipping_address_id: this.selectedShippingAddress.id,
        shipping_method_id: this.$store.getters['cart/shippingMethodId'],
        payment_method: this.paymentMethod.slug,
        pth_code_payment_method: this.paymentMethod.pth_code,
      };

      if (this.availableCoupon) {
        order.coupon_code = this.availableCoupon.code;
        order.discount_value = this.availableCoupon.value;
      }

      if (this.paymentMethod.slug === 'stripe' || this.paymentMethod.slug === 'sepa') {
        order.stripe_payment_method_id = this.stripePaymentMethod;
      }

      if ((this.paymentMethod.slug === 'stripe' || this.paymentMethod.slug === 'sepa') && (this.stripePaymentMethod === 'last' || !this.stripePaymentMethod)) {
        this.createNotification('error', `${this.$t('stripe.enterPaymentMethod')}`);
        this.workingOn = false;
        return null;
      }

      if (this.paymentMethod.fee) {
        order.fee = this.paymentMethod.fee;
      }

      return createOrder(this.customer, order);
    },

    saveOrder() {
      this.workingOn = true;
      this.message = this.$t('checkout.workingOn');
      this.persistOrder().then(({ data }) => {
        this.orderSaved = data;
        addOrderDetails(data, this.cartProducts)
          .then(() => {
            if (this.totalToPay <= 0) {
              axios.put(`/api/customers/${this.user.customer.id}/coupon/${this.availableCoupon.code}/redeem`, {
                total_paid: this.availableCoupon.type === 'V' ? this.totalWithoutCoupon : null,
              }).then(() => {
                this.setOrderIsRegistered(this.orderSaved);
              });
              return;
            }
            this.payOrder(data);
          }).catch((error) => {
            this.workingOn = false;
            this.deleteTestataAndTryAgain(error);
          });
      }).catch((error) => {
        this.workingOn = false;
        this.checkSessionErrorNotification(error);
      });
    },

    async payOrder(order) {
      if ((this.paymentMethod.slug === 'stripe' || this.paymentMethod.slug === 'sepa') && (this.stripePaymentMethod === 'last' || !this.stripePaymentMethod)) {
        this.createNotification('error', `${this.$t('stripe.enterPaymentMethod')}`);
        this.workingOn = false;
        return;
      }
      this.workingOn = true;
      this.message = this.$t(`payments.${this.paymentMethod.slug}-msg`);
      if (this.availableCoupon) {
        await axios.put(`/api/customers/${this.user.customer.id}/coupon/${this.availableCoupon.code}/redeem`, {
          total_paid: this.availableCoupon.type === 'V' ? this.totalWithoutCoupon : null,
        });
      }
      axios.post(`/api/orders/${order.id}/payment`)
        .then((response) => {
          this.$store.dispatch('cart/updatePaymentMethod', this.paymentMethod);
          if (this.paymentMethod.slug === 'paypal') {
            this.loadPaypal(response.data);
            this.$store.dispatch('cart/cartToReset');
            return;
          }
          if (this.paymentMethod.slug === 'stripe' && response.data.error) {
            this.checkTheErrorType(response.data, order);
            return;
          }
          if (this.paymentMethod.slug === 'sofort') {
            this.loadSofort(response.data, order);
            this.$store.dispatch('cart/cartToReset');
            return;
          }
          this.goToOrderView(order);
        }).catch(() => {
          this.goToOrderView(order);
          this.setOrderPaymentHasFailed(order);
          this.createNotification('error', `${this.$t('notifications.errorPayment')}`);
        });
    },

    checkTheErrorType(paymentResult, order) {
      if (paymentResult.paymentIntent) {
        this.getAuthenticationToConfirmPayment(paymentResult.paymentIntent, order);
        return;
      }
      this.workingOn = false;
      this.createNotification('error', paymentResult.error.message);
      this.goToOrderView(order);
    },

    getAuthenticationToConfirmPayment(paymentIntent, order) {
      this.message = this.$t('stripe.authRequired');

      stripe.confirmCardPayment(paymentIntent.client_secret, {
        payment_method: paymentIntent.last_payment_error.payment_method.id,
      }).then((result) => {
        if (result.error) {
          this.workingOn = false;
          this.createNotification('error', `${result.error.message}`);
          this.setOrderPaymentHasFailed(order);
          return;
        }
        this.setOrderHasBeenPaid(order, paymentIntent.amount);
      });
    },

    setOrderHasBeenPaid(order, amount) {
      axios.put(`/api/orders/${order.id}/paid`, { amount }).then(() => {
        this.goToOrderView(order);
      }).catch(() => {
        this.workingOn = false;
        this.goToOrderView(order);
        // notifica pagamento accettato ?
        // mail a noi che l'ordine è stato pagato ma è ancora settato come in sospeso
      });
    },

    setOrderPaymentHasFailed(order) {
      axios.put(`/api/orders/${order.id}/fail`).then(() => {
        this.goToOrderView(order);
      }).catch(() => {
        this.workingOn = false;
        this.goToOrderView(order);
      });
    },

    setOrderIsRegistered(order) {
      axios.put(`/api/orders/${order.id}/register`).then(() => {
        this.goToOrderView(order);
      }).catch(() => {
        this.workingOn = false;
        this.goToOrderView(order);
      });
    },

    goToOrderView(order) {
      this.$router.push({ name: 'single-order', params: { order_no: order.order_no } }).catch(() => {
        this.$router.go();
      });
      this.$store.dispatch('cart/reset');
    },

    deleteTestataAndTryAgain(error) {
      this.checkSessionErrorNotification(error);
      axios.delete(`/api/orders/${this.orderSaved.id}`).catch(() => {
        // che facciamo?
      });
    },

    loadPaypal(path) {
      window.location.href = path;
    },

    loadSofort(paymentIntent, order) {
      stripe.confirmSofortPayment(
        paymentIntent.client_secret,
        {
          payment_method: {
            sofort: {
              country: order.billing_address.country,
            },
          },
          return_url: `${process.env.VUE_APP_APP_URL}/profile/orders/${order.order_no}`,
        },
      ).then((result) => {
        if (result.error) {
          this.workingOn = false;
          this.createNotification('error', `${result.error.message}`);
          this.setOrderPaymentHasFailed(order);
        }
      });
    },
  },
};
</script>
